import { useEffect, useState } from 'react';
import { decode } from '../Services/DecodeToken';

// Stale keys referencing renamed/removed columns are safe — unknown fields in
// columnVisibilityModel, orderedFields, and columnWidths are silently ignored
// at render time.

type VisibilityModel = Record<string, boolean>;
type WidthModel = Record<string, number>;

interface StoredPreferences {
  visibility: VisibilityModel;
  orderedFields: string[];
  columnWidths: WidthModel;
}

export interface UseColumnPreferencesResult {
  columnVisibilityModel: VisibilityModel;
  onColumnVisibilityModelChange: (model: VisibilityModel) => void;
  handleSave: (columnWidthsOverride?: WidthModel) => void;
  handleReset: () => void;
  hasSavedPreference: boolean;
  isModelDirty: boolean;
  orderedFields: string[];
  onOrderChange: (fields: string[]) => void;
  columnWidths: WidthModel;
  onColumnWidthChange: (field: string, width: number) => void;
  touchWidths: () => void;
}

const canUseLocalStorage = (): boolean => {
  try {
    const probe = '__aims_ls_probe__';
    localStorage.setItem(probe, probe);
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
};

const parseColumnWidths = (raw: unknown): WidthModel => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const result: WidthModel = {};
  for (const [field, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      result[field] = Math.round(value);
    }
  }
  return result;
};

const getStorageKey = (pageKey: string | undefined): string | null => {
  const token = decode() as any;
  const userId: string | undefined = token?.userId;
  return pageKey && userId ? `aims_col_prefs_${userId}_${pageKey}` : null;
};

const readStoredPreferences = (storageKey: string | null): StoredPreferences | null => {
  if (!storageKey || !canUseLocalStorage()) return null;
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      localStorage.removeItem(storageKey);
      return null;
    }
    if ('visibility' in parsed) {
      const { visibility, orderedFields: order, columnWidths: widths } = parsed as StoredPreferences;
      return {
        visibility: visibility ?? {},
        orderedFields: Array.isArray(order) ? order : [],
        columnWidths: parseColumnWidths(widths),
      };
    }
    // Old format: plain visibility object
    return {
      visibility: parsed as VisibilityModel,
      orderedFields: [],
      columnWidths: {},
    };
  } catch {
    try { localStorage.removeItem(storageKey); } catch {}
    return null;
  }
};

export const useColumnPreferences = (
  pageKey: string | undefined,
  defaultVisibility: VisibilityModel = {}
): UseColumnPreferencesResult => {
  const storageKey = getStorageKey(pageKey);

  const [initialStored] = useState(() => readStoredPreferences(storageKey));
  const initialVisibility = initialStored?.visibility ?? defaultVisibility;
  const initialOrder = initialStored?.orderedFields ?? [];
  const initialWidths = initialStored?.columnWidths ?? {};

  const [model, setModel] = useState<VisibilityModel>(initialVisibility);
  const [savedModel, setSavedModel] = useState<VisibilityModel>(initialVisibility);
  const [orderedFields, setOrderedFields] = useState<string[]>(initialOrder);
  const [savedOrderedFields, setSavedOrderedFields] = useState<string[]>(initialOrder);
  const [columnWidths, setColumnWidths] = useState<WidthModel>(initialWidths);
  const [savedColumnWidths, setSavedColumnWidths] = useState<WidthModel>(initialWidths);
  const [widthsTouched, setWidthsTouched] = useState(false);

  useEffect(() => {
    const stored = readStoredPreferences(storageKey);
    setModel(stored?.visibility ?? defaultVisibility);
    setSavedModel(stored?.visibility ?? defaultVisibility);
    setOrderedFields(stored?.orderedFields ?? []);
    setSavedOrderedFields(stored?.orderedFields ?? []);
    setColumnWidths(stored?.columnWidths ?? {});
    setSavedColumnWidths(stored?.columnWidths ?? {});
    setWidthsTouched(false);
  }, [storageKey]);

  const onColumnVisibilityModelChange = (newModel: VisibilityModel) => {
    setModel(newModel);
  };

  const onOrderChange = (fields: string[]) => {
    setOrderedFields(fields);
  };

  const onColumnWidthChange = (field: string, width: number) => {
    if (!field || !Number.isFinite(width) || width <= 0) return;
    const nextWidth = Math.round(width);
    setColumnWidths((prev) => {
      if (prev[field] === nextWidth) return prev;
      return { ...prev, [field]: nextWidth };
    });
    setWidthsTouched((prev) => (prev ? prev : true));
  };

  const touchWidths = () => {
    setWidthsTouched((prev) => (prev ? prev : true));
  };

  const handleSave = (columnWidthsOverride?: WidthModel) => {
    if (!storageKey || !canUseLocalStorage()) return;
    const widthsToStore = columnWidthsOverride ?? columnWidths;
    try {
      const toStore: StoredPreferences = {
        visibility: model,
        orderedFields,
        columnWidths: widthsToStore,
      };
      localStorage.setItem(storageKey, JSON.stringify(toStore));
      setSavedModel(model);
      setSavedOrderedFields(orderedFields);
      setColumnWidths(widthsToStore);
      setSavedColumnWidths(widthsToStore);
      setWidthsTouched(false);
    } catch (err) {
      console.warn('Failed to save column preferences:', err);
    }
  };

  const handleReset = () => {
    setModel(defaultVisibility);
    setSavedModel(defaultVisibility);
    setOrderedFields([]);
    setSavedOrderedFields([]);
    setColumnWidths({});
    setSavedColumnWidths({});
    setWidthsTouched(false);
    if (!storageKey || !canUseLocalStorage()) return;
    try { localStorage.removeItem(storageKey); } catch {}
  };

  const hasSavedPreference =
    JSON.stringify(savedModel) !== JSON.stringify(defaultVisibility) ||
    savedOrderedFields.length > 0 ||
    Object.keys(savedColumnWidths).length > 0;

  const isModelDirty =
    widthsTouched ||
    JSON.stringify(model) !== JSON.stringify(savedModel) ||
    JSON.stringify(orderedFields) !== JSON.stringify(savedOrderedFields) ||
    JSON.stringify(columnWidths) !== JSON.stringify(savedColumnWidths);

  return {
    columnVisibilityModel: model,
    onColumnVisibilityModelChange,
    handleSave,
    handleReset,
    hasSavedPreference,
    isModelDirty,
    orderedFields,
    onOrderChange,
    columnWidths,
    onColumnWidthChange,
    touchWidths,
  };
};
